import type { StatusTone } from '@/lib/status';
import type { TodayChip, TodayItem, TodayRef, TodayResponse } from '@/api/today';

/**
 * Today grouped as Matt chose (2026-10-29): one item per journey. An order
 * packet carries its journey's other open asks nested under it; everything
 * else stands alone. The server's ranking order is kept.
 */
export interface TodayGroup {
  item: TodayItem;
  related: TodayItem[];
}

export function groupToday(today: TodayResponse | null): TodayGroup[] {
  if (!today) return [];
  const byAskId = new Map(today.items.filter((it) => it.ask).map((it) => [it.ask!.id, it]));
  const nested = new Set<string>();
  const groups: TodayGroup[] = [];
  for (const item of today.items) {
    if (item.source !== 'order') continue;
    for (const id of item.related_asks ?? []) {
      if (byAskId.has(id)) nested.add(byAskId.get(id)!.key);
    }
  }
  for (const item of today.items) {
    if (nested.has(item.key)) continue;
    const related = item.source === 'order'
      ? (item.related_asks ?? []).map((id) => byAskId.get(id)).filter((r): r is TodayItem => Boolean(r))
      : [];
    groups.push({ item, related });
  }
  return groups;
}

/** A journey's order card on Today: `/today#order-<id>` scrolls to it. */
export const todayOrderAnchor = (journeyId: string) => `order-${journeyId}`;

/** The journeys that have an order card on Today. */
export function todayOrderJourneys(today: TodayResponse | null): Set<string> {
  return new Set((today?.items ?? []).filter((it) => it.source === 'order' && it.journey).map((it) => it.journey!.id));
}

/** The sidebar's number: items Matt sees at the top level. */
export function todayCount(today: TodayResponse | null): number | undefined {
  return today ? groupToday(today).length : undefined;
}

export const KIND_LABEL: Record<TodayItem['source'], string> = {
  order: 'Place an order',
  ask: 'Decision',
  setup_submit: 'Submit in Setup Preview',
  discrepancies: 'Count differences',
  desk: 'Desk session',
};

/** "Vendor · release: what" → its parts; an ask's own title is the whole ask. */
export function titleParts(item: TodayItem): { vendor: string | null; release: string | null; ask: string } {
  const at = item.title.lastIndexOf(': ');
  if (item.source === 'ask' || item.source === 'desk' || at < 0) return { vendor: null, release: null, ask: item.title };
  const [vendor, ...rest] = item.title.slice(0, at).split(' · ');
  const what = item.title.slice(at + 2);
  return { vendor: vendor || null, release: rest.join(' · ') || null, ask: what.charAt(0).toUpperCase() + what.slice(1) };
}

/** Late is red; due within three days needs you; money and age are plain facts. */
export function chipTone(chip: TodayChip): StatusTone {
  if (chip.kind !== 'due' || chip.days == null) return 'neutral';
  if (chip.days < 0) return 'danger';
  return chip.days <= 3 ? 'warning' : 'neutral';
}

export function isLate(item: TodayItem): boolean {
  return item.why.some((c) => c.kind === 'due' && (c.days ?? 0) < 0);
}

/** Where a fact's ref opens: an in-app route, an outside link, or a mail snapshot. */
export type RefTarget =
  | { kind: 'route'; to: string }
  | { kind: 'external'; href: string }
  | { kind: 'mail'; messageId: string }
  | null;

const SETUP_PREVIEW_URL = 'https://tools.acherryontop.com/setup-preview';

export function refTarget(ref: TodayRef | undefined, item: TodayItem): RefTarget {
  if (!ref) return null;
  const journeyId = item.journey?.id;
  switch (ref.type) {
    case 'journey': return ref.id ? { kind: 'route', to: `/journeys/${ref.id}` } : null;
    case 'draft': return journeyId ? { kind: 'route', to: `/journeys/${journeyId}?tab=draft` } : null;
    case 'document': return journeyId ? { kind: 'route', to: `/journeys/${journeyId}?tab=documents` } : null;
    case 'receiving': return journeyId ? { kind: 'route', to: `/journeys/${journeyId}?tab=receiving` } : null;
    case 'message': return ref.id ? { kind: 'mail', messageId: ref.id } : null;
    case 'setup_preview': return ref.id ? { kind: 'external', href: `${SETUP_PREVIEW_URL}?folder=${encodeURIComponent(ref.id)}` } : null;
    case 'product': return ref.id ? { kind: 'external', href: `https://backend.acherryontop.com/product/${encodeURIComponent(ref.id)}` } : null;
    case 'url': return ref.url ? { kind: 'external', href: ref.url } : null;
    default: return null;
  }
}

/** What a queued answer's run is doing, in words. */
export const RUN_STATE_LABEL: Record<string, string> = {
  waiting: 'Waiting for the next run',
  queued: 'Queued — runs when the Mac is awake',
  running: 'Running',
  finished: 'Done',
};
